import React from 'react';
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation
} from 'react-router-dom';
import { APP_KEYS } from '../common/consts';
import { AuthPageContainer } from '../auth';
import { LandingPageContainer } from '../landing';
import { TouchPosePageContainer } from '../home';
import { Header } from '../common/components/header/Header.component';
import { SettingsPageContainer } from '../settings';
import useAuth from '../auth/feature/auth';
import { Loader, NotFound } from '../common/components';
import { ManagementContainer } from '../management';
import useLoader from '../common/feature/loader';
import ModelsListContainer from '../library/list/ModelsList';
import ModelPageContainer from '../library/item-details/ModelsPage';
import Footer from '../common/components/footer/Footer.component';
import ScrollToTop from '../common/components/scroll-to-top.component';
import DocsLayout from '../touchpose/docs/layout';
import DocsLanding from '../touchpose/docs/landing';
import TouchposeEditorDoc from '../touchpose/docs/editor';
import TouchposeGettingStartedDoc from '../touchpose/docs/getting-started';
import GetTouchposeDoc from '../touchpose/docs/get-touchpose';
import LoadingTouchFileDoc from '../touchpose/docs/loading-touchfile';
import LoadingTouchSetsDoc from '../touchpose/docs/loading-touchsets';
import ActionsMenusDoc from '../touchpose/docs/actions-menus';
import ActionsDoc from '../touchpose/docs/actions';
import MayaMenuDoc from '../touchpose/docs/maya-menu';
import ApiDoc from '../touchpose/docs/api';
import AnimatingDoc from '../touchpose/docs/animating';
import PreferencesDoc from '../touchpose/docs/preferences';
import DrawGroupsDoc from '../touchpose/docs/draw-groups';
import DrawingPosesDoc from '../touchpose/docs/drawing-poses';
import UnrealDoc from '../touchpose/docs/unreal';
import TouchposeTutorials from '../touchpose/tutorials';
import TouchposeChangelog from '../touchpose/changelog';
import TouchposeRoadmap from '../touchpose/roadmap';
import TouchposeFaq from '../touchpose/faq';
import ContactPage from '../contact';
import RiggingServicesPage from '../rigging-services';
import SquarebitSubdivsPage from '../squarebit-subdivs';
import SquarebitEyePage from '../squarebit-eye';
import StandbyPage from '../standby';
import RiserPage from '../riser';
import useAccessRealtime from '../common/feature/access-realtime';
import EyeChangelog from '../squarebit-eye/changelog';
import SbFrameworkPage from '../sbframework';
import SbFrameworkDocsLayout from '../sbframework/docs/layout';
import SbFrameworkDocsLanding from '../sbframework/docs/landing';
import SbFrameworkInstallDoc from '../sbframework/docs/install';
import SbFrameworkTemplatesDoc from '../sbframework/docs/templates';
import SbFrameworkNxtDoc from '../sbframework/docs/nxt';
import SbFrameworkApiDoc from '../sbframework/docs/api';
import SbFrameworkApiModuleDoc from '../sbframework/docs/api-module';
import FrameworkChangelog from '../sbframework/changelog';
import NxtChangelog from '../sbframework/nxt-changelog';
import EyeDocsLayout from '../squarebit-eye/docs/layout';
import EyeDocsLanding from '../squarebit-eye/docs/landing';
import EyeInstallationDoc from '../squarebit-eye/docs/installation';
import EyeGettingStartedDoc from '../squarebit-eye/docs/getting-started';
import EyeSharingLooksDoc from '../squarebit-eye/docs/sharing-looks';
import EyeParametersDoc from '../squarebit-eye/docs/parameters';
import EyeTexturesDoc from '../squarebit-eye/docs/textures';
import EyeRiggingDoc from '../squarebit-eye/docs/rigging';
import SubdivsChangelog from '../squarebit-subdivs/changelog';
import SubdivsDocsLayout from '../squarebit-subdivs/docs/layout';
import SubdivsDocsLanding from '../squarebit-subdivs/docs/landing';
import SubdivsInstallationDoc from '../squarebit-subdivs/docs/installation';
import SubdivsLicensingDoc from '../squarebit-subdivs/docs/licensing';
import SubdivsGeometryCachesDoc from '../squarebit-subdivs/docs/geometry-caches';
import SubdivsSkeletalMeshesDoc from '../squarebit-subdivs/docs/skeletal-meshes';
import SubdivsStaticMeshesDoc from '../squarebit-subdivs/docs/static-meshes';
import SubdivsGroomsDoc from '../squarebit-subdivs/docs/grooms';
import { AdminPageContainer } from '../admin';
import { AdminMetricsPageContainer } from '../admin/metrics-page';
import { AdminLicenseActivityPageContainer } from '../admin/license-activity-page';
import StoreChangelog from '../admin/changelog';
import { TermsPage } from '../legal/terms';
import { PrivacyPage } from '../legal/privacy';
import { EulaPage } from '../legal/eula';
import { TouchposeEulaPage } from '../legal/touchpose-eula';
import { EyeEulaPage } from '../legal/eye-eula';
import { SbFrameworkEulaPage } from '../legal/sbframework-eula';
import { CookieConsent } from '../common/components/cookie-consent.component';
import { NavEngagementProvider } from '../common/feature/nav-engagement';

export function MainRouter() {
  return (
    <Router>
      <ScrollToTop />
      <AppShell />
      <CookieConsent />
    </Router>
  );
}

// Split out of MainRouter so it can read the current route: the landing page is
// the full-window reel, which escapes the centered content column and drops the
// footer. Every other page keeps the standard container.
function AppShell() {
  const { isAuthenticated, isAdmin } = useAuth();
  const { showLoader } = useLoader();
  const { pathname } = useLocation();

  // An admin granting or revoking a product updates this session in place
  // rather than waiting for the next reload.
  useAccessRealtime();

  const isLanding = pathname === APP_KEYS.ROUTER_LINKS.ROOT;
  // Standby opens on a full-window reel like the landing page does, so it also
  // has to escape the centered content column — inside it the video is a boxed
  // panel with the page showing either side. It keeps the footer, though:
  // unlike the landing page it is something you scroll to the end of.
  const isFullBleed = isLanding || pathname === APP_KEYS.ROUTER_LINKS.STANDBY;

  return (
    // Engaging the nav thickens the header's fill and dims the reel behind it.
    // Both sides read that from here, and only the landing page listens for it.
    <NavEngagementProvider active={isFullBleed}>
      {showLoader && <Loader />}
      <Header />
      <div
        id="main-content"
        className={
          isFullBleed
            ? 'relative w-full'
            : 'flex max-w-[1300px] relative mx-auto justify-center flex-col main-router-container w-[90%]'
        }
      >
        <Routes>
          {/* Signed-in visitors have no business on the auth forms. This stays
              an element rather than a redirect: activation flips isAuthenticated
              before it navigates on to /management, and a redirect here would
              race it. */}
          <Route
            element={
              isAuthenticated ? <TouchPosePageContainer /> : <AuthPageContainer />
            }
            path={APP_KEYS.ROUTER_KEYS.AUTH}
          />
          <Route
            element={<LandingPageContainer />}
            path={APP_KEYS.ROUTER_KEYS.ROOT}
          />
          <Route
            element={<ModelsListContainer />}
            path={APP_KEYS.ROUTER_KEYS.MODELS}
          />
          <Route
            element={<ModelPageContainer />}
            path={APP_KEYS.ROUTER_KEYS.MODEL_DETAILS}
          />
          <Route
            element={
              isAuthenticated ? <SettingsPageContainer /> : <AuthPageContainer />
            }
            path={APP_KEYS.ROUTER_KEYS.SETTINGS}
          />
          <Route
            element={
              isAuthenticated ? <ManagementContainer /> : <AuthPageContainer />
            }
            path={APP_KEYS.ROUTER_KEYS.MANAGEMENT}
          />
          {/* TouchPose used to be the home page. It now has its own product
              page, matching how Subdivs and Eye are laid out. */}
          <Route
            path={APP_KEYS.ROUTER_KEYS.TOUCHPOSE}
            element={<TouchPosePageContainer />}
          />
          <Route path={APP_KEYS.ROUTER_KEYS.TOUCHPOSE_DOCS} element={<DocsLayout />}>
            <Route index element={<DocsLanding />} />
            <Route path="get-touchpose" element={<GetTouchposeDoc />} />
            <Route path="getting-started" element={<TouchposeGettingStartedDoc />} />
            <Route path="loading-touchfile" element={<LoadingTouchFileDoc />} />
            <Route path="editor" element={<TouchposeEditorDoc />} />
            <Route path="loading-touchsets" element={<LoadingTouchSetsDoc />} />
            <Route path="maya-menu" element={<MayaMenuDoc />} />
            <Route path="actions-menus" element={<ActionsMenusDoc />} />
            <Route path="actions" element={<ActionsDoc />} />
            <Route path="api" element={<ApiDoc />} />
            <Route path="animating" element={<AnimatingDoc />} />
            <Route path="preferences" element={<PreferencesDoc />} />
            <Route path="draw-groups" element={<DrawGroupsDoc />} />
            <Route path="drawing-poses" element={<DrawingPosesDoc />} />
            <Route path="unreal" element={<UnrealDoc />} />
          </Route>
          <Route
            path={APP_KEYS.ROUTER_KEYS.TOUCHPOSE_TUTORIALS}
            element={<TouchposeTutorials />}
          />
          <Route
            path={APP_KEYS.ROUTER_KEYS.TOUCHPOSE_CHANGELOG}
            element={<TouchposeChangelog />}
          />
          <Route
            path={APP_KEYS.ROUTER_KEYS.TOUCHPOSE_ROADMAP}
            element={<TouchposeRoadmap />}
          />
          <Route
            path={APP_KEYS.ROUTER_KEYS.TOUCHPOSE_FAQ}
            element={<TouchposeFaq />}
          />
          <Route path={APP_KEYS.ROUTER_KEYS.CONTACT} element={<ContactPage />} />
          <Route
            path={APP_KEYS.ROUTER_KEYS.RIGGING_SERVICES}
            element={<RiggingServicesPage />}
          />
          <Route
            path={APP_KEYS.ROUTER_KEYS.SQUAREBIT_SUBDIVS}
            element={<SquarebitSubdivsPage />}
          />
          <Route
            path={APP_KEYS.ROUTER_KEYS.SQUAREBIT_EYE}
            element={<SquarebitEyePage />}
          />
          {/* Registered unconditionally: the page itself renders NotFound for
              anyone without a 'standby' grant. Hiding the route instead would
              be no stronger — the bundle reaches every visitor either way —
              and the real gate is the API, which serves the copy and the
              media only to granted accounts. */}
          <Route path={APP_KEYS.ROUTER_KEYS.STANDBY} element={<StandbyPage />} />
          {/* Riser, same arrangement and a real gate behind it. The page has
              no copy of its own: it asks an endpoint that answers only an
              account holding the 'riser' grant, and renders NotFound when it
              gets nothing. Registering the route is not a disclosure the
              bundle does not already make, and hiding it would not stop
              anyone reading the router. */}
          <Route path={APP_KEYS.ROUTER_KEYS.RISER} element={<RiserPage />} />
          <Route
            path={APP_KEYS.ROUTER_KEYS.SQUAREBIT_EYE_CHANGELOG}
            element={<EyeChangelog />}
          />
          {/* Framework, same arrangement as Riser above: registered
              unconditionally, and the page itself renders NotFound for an
              account without the 'sbframework' grant. What is actually
              protected is the download and the key, and both of those are
              checked server-side. */}
          <Route
            path={APP_KEYS.ROUTER_KEYS.SBFRAMEWORK}
            element={<SbFrameworkPage />}
          />
          <Route
            path={APP_KEYS.ROUTER_KEYS.SBFRAMEWORK_DOCS}
            element={<SbFrameworkDocsLayout />}
          >
            <Route index element={<SbFrameworkDocsLanding />} />
            <Route path="install" element={<SbFrameworkInstallDoc />} />
            <Route path="templates" element={<SbFrameworkTemplatesDoc />} />
            <Route path="nxt" element={<SbFrameworkNxtDoc />} />
            {/* The search page over all three packages, and the front door to
                the reference. Same component: with no package it searches
                everything, with one it lists that package. */}
            <Route path="api" element={<SbFrameworkApiDoc />} />
            <Route path="api/:package" element={<SbFrameworkApiDoc />} />
            {/* The dotted module name is one segment - `api/core/core.maya.psd`.
                Two explicit routes rather than an optional `:module?`, which
                react-router handles poorly when the segment contains dots. */}
            <Route
              path="api/:package/:module"
              element={<SbFrameworkApiModuleDoc />}
            />
          </Route>
          <Route
            path={APP_KEYS.ROUTER_KEYS.SBFRAMEWORK_CHANGELOG}
            element={<FrameworkChangelog />}
          />
          <Route
            path={APP_KEYS.ROUTER_KEYS.SBFRAMEWORK_NXT_CHANGELOG}
            element={<NxtChangelog />}
          />
          <Route
            path={APP_KEYS.ROUTER_KEYS.SQUAREBIT_EYE_DOCS}
            element={<EyeDocsLayout />}
          >
            <Route index element={<EyeDocsLanding />} />
            <Route path="getting-started" element={<EyeGettingStartedDoc />} />
            <Route path="installation" element={<EyeInstallationDoc />} />
            <Route path="sharing-looks" element={<EyeSharingLooksDoc />} />
            <Route path="parameters" element={<EyeParametersDoc />} />
            <Route path="textures" element={<EyeTexturesDoc />} />
            <Route path="rigging" element={<EyeRiggingDoc />} />
          </Route>
          <Route
            path={APP_KEYS.ROUTER_KEYS.SQUAREBIT_SUBDIVS_CHANGELOG}
            element={<SubdivsChangelog />}
          />
          <Route
            path={APP_KEYS.ROUTER_KEYS.SQUAREBIT_SUBDIVS_DOCS}
            element={<SubdivsDocsLayout />}
          >
            <Route index element={<SubdivsDocsLanding />} />
            <Route path="installation" element={<SubdivsInstallationDoc />} />
            <Route path="licensing" element={<SubdivsLicensingDoc />} />
            <Route path="geometry-caches" element={<SubdivsGeometryCachesDoc />} />
            <Route path="skeletal-meshes" element={<SubdivsSkeletalMeshesDoc />} />
            <Route path="static-meshes" element={<SubdivsStaticMeshesDoc />} />
            <Route path="grooms" element={<SubdivsGroomsDoc />} />
            {/* Old combined page → redirect in-the-wild links to a component page */}
            <Route
              path="using-the-component"
              element={
                <Navigate to="/squarebit-subdivs/docs/skeletal-meshes" replace />
              }
            />
          </Route>
          {/* Legacy route → redirect old in-the-wild links to the new product page */}
          <Route
            path={APP_KEYS.ROUTER_KEYS.SQUAREBIT_SUBDIVS_LEGACY}
            element={
              <Navigate to={APP_KEYS.ROUTER_LINKS.SQUAREBIT_SUBDIVS} replace />
            }
          />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          {/* Per-product EULAs. The bare /eula URL predates the split and may
              be cached/linked externally — keep it as a redirect to Subdivs. */}
          <Route path="/squarebit-subdivs/eula" element={<EulaPage />} />
          <Route path="/touchpose/eula" element={<TouchposeEulaPage />} />
          <Route path="/squarebit-eye/eula" element={<EyeEulaPage />} />
          <Route
            path={APP_KEYS.ROUTER_KEYS.SBFRAMEWORK_EULA}
            element={<SbFrameworkEulaPage />}
          />
          <Route
            path="/eula"
            element={<Navigate to="/squarebit-subdivs/eula" replace />}
          />
          {/* Non-admins go to the landing page. It is full-bleed, so it has to
              be a redirect rather than an element rendered in this column. */}
          <Route
            element={
              isAdmin ? (
                <AdminPageContainer />
              ) : (
                <Navigate to={APP_KEYS.ROUTER_LINKS.ROOT} replace />
              )
            }
            path={APP_KEYS.ROUTER_KEYS.ADMIN}
          />
          <Route
            element={
              isAdmin ? (
                <AdminMetricsPageContainer />
              ) : (
                <Navigate to={APP_KEYS.ROUTER_LINKS.ROOT} replace />
              )
            }
            path={APP_KEYS.ROUTER_KEYS.ADMIN_METRICS}
          />
          <Route
            element={
              isAdmin ? (
                <AdminLicenseActivityPageContainer />
              ) : (
                <Navigate to={APP_KEYS.ROUTER_LINKS.ROOT} replace />
              )
            }
            path={APP_KEYS.ROUTER_KEYS.ADMIN_LICENSE_ACTIVITY}
          />
          <Route
            element={
              isAdmin ? (
                <StoreChangelog />
              ) : (
                <Navigate to={APP_KEYS.ROUTER_LINKS.ROOT} replace />
              )
            }
            path={APP_KEYS.ROUTER_KEYS.ADMIN_CHANGELOG}
          />
          <Route
            path={APP_KEYS.ROUTER_KEYS.NOT_FOUND_CATCHALL}
            element={<NotFound />}
          />
        </Routes>
        {/* The footer has no width of its own — it takes the content column
            from this container. On a full-bleed page that container is the
            whole window, so the footer stretched edge to edge and its rule ran
            the full width of the screen, matching no other page on the site.
            Give it the column back explicitly. Not applied on the normal pages:
            there the parent already is the column, and nesting a second w-[90%]
            inside it would indent the footer to 90% of 90%. */}
        {!isLanding &&
          (isFullBleed ? (
            <div className="mx-auto w-[90%] max-w-[1300px]">
              <Footer />
            </div>
          ) : (
            <Footer />
          ))}
      </div>
    </NavEngagementProvider>
  );
}
